import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAppLogo } from '../hooks/useAppLogo';
import { API_BASE_URL } from '../utils/apiConfig';
import '../styles-admin-unified-theme.css';
import './TournamentAdminLayout.css';
import '../styles-dynamic-mode.css';
import '../styles-tournament-fullwidth.css';

function TournamentAdminLayout({ children, fullSize = false, hideSidebar = false, hideHeader = false, noLayoutClasses = false }) {
  // Sidebar removed - navigation moved to header
  const [tournament, setTournament] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [countdown, setCountdown] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [quickStats, setQuickStats] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [showMobileNavDropdown, setShowMobileNavDropdown] = useState(false);
  const [favoriteItems, setFavoriteItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('favoriteSidebarItems') || '[]');
    } catch {
      return [];
    }
  });
  const searchInputRef = useRef(null);
  const profileMenuRef = useRef(null);
  const profileTriggerRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const [profileDropdownPosition, setProfileDropdownPosition] = useState({ top: 0, right: 0 });
  const notificationsRef = useRef(null);
  const quickActionsRef = useRef(null);
  const mobileNavDropdownRef = useRef(null);
  const [user] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { code } = useParams();
  const { logoUrl } = useAppLogo();
  
  // Hide top banner on live auction page and grouping page (show banner on auction-results)
  const isAuctionPage = location.pathname.includes('/auction') && !location.pathname.includes('auction-results');
  const isGroupingPage = location.pathname.includes('/grouping');
  const normalizedRoleKey = (user?.role || '')
    .toString()
    .trim()
    .replace(/[\s_]/g, '')
    .toLowerCase();
  // eslint-disable-next-line no-unused-vars
  const isSuperAdmin = normalizedRoleKey === 'superadmin';
  const isTournamentManager = normalizedRoleKey === 'tournamentmanager';
  const isAuctionController = normalizedRoleKey === 'auctioncontroller';

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setShowMobileNavDropdown(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchQuickStats = useCallback(async () => {
    if (!code) return;
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [playersRes, teamsRes, auctionRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/players/${code}`, { headers }).catch(() => ({ data: { players: [] } })),
        axios.get(`${API_BASE_URL}/api/teams/${code}`, { headers }).catch(() => ({ data: { teams: [] } })),
        axios.get(`${API_BASE_URL}/api/auctions/summary/${code}`, { headers }).catch(() => ({ data: { summary: null } }))
      ]);

      const players = playersRes.data.players || [];
      const teams = teamsRes.data.teams || [];
      const auctionSummary = auctionRes.data.summary;

      setQuickStats({
        players: players.length,
        teams: teams.length,
        auctionProgress: auctionSummary ? Math.round((auctionSummary.soldPlayers / auctionSummary.totalPlayers) * 100) : 0,
        totalBids: auctionSummary?.totalBids || 0,
        averageBid: auctionSummary?.averageBid || 0,
        registeredToday: players.filter(p => {
          const registered = new Date(p.createdAt);
          const today = new Date();
          return registered.toDateString() === today.toDateString();
        }).length
      });
    } catch (err) {
      console.error('Error fetching quick stats:', err);
    }
  }, [code]);

  const fetchNotifications = useCallback(async () => {
    if (!code) return;
    try {
      const mockNotifications = [
        { id: 1, type: 'info', message: 'New player registration pending approval', time: '5m ago', read: false },
        { id: 2, type: 'success', message: 'Auction completed successfully', time: '1h ago', read: false },
        { id: 3, type: 'warning', message: 'Team registration deadline approaching', time: '2h ago', read: true },
      ];
      setNotifications(mockNotifications);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  }, [code]);

  useEffect(() => {
    if (tournament) {
      fetchQuickStats();
      fetchNotifications();
      const interval = setInterval(() => {
        fetchQuickStats();
        fetchNotifications();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [tournament, fetchQuickStats, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on the mobile dropdown button, wrapper, or menu
      const clickedElement = event.target;
      const isMobileDropdownElement = 
        clickedElement.closest('.admin-nav-dropdown-wrapper') !== null ||
        clickedElement.closest('.admin-nav-dropdown-btn') !== null ||
        clickedElement.closest('.admin-nav-dropdown-menu') !== null ||
        clickedElement.classList.contains('admin-nav-dropdown-btn') ||
        clickedElement.classList.contains('admin-nav-dropdown-menu') ||
        (mobileNavDropdownRef.current && mobileNavDropdownRef.current.contains(clickedElement));
      
      if (isMobileDropdownElement) {
        // Don't close mobile dropdown if clicking inside it
        return;
      }
      
      // Close profile menu if clicking outside trigger and outside portal dropdown
      const inProfileTrigger = profileMenuRef.current && profileMenuRef.current.contains(clickedElement);
      const inProfileDropdown = profileDropdownRef.current && profileDropdownRef.current.contains(clickedElement);
      if (!inProfileTrigger && !inProfileDropdown) {
        setShowProfileMenu(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(clickedElement)) {
        setShowNotifications(false);
      }
      if (quickActionsRef.current && !quickActionsRef.current.contains(clickedElement)) {
        setShowQuickActions(false);
      }
      
      // Close mobile dropdown if clicking outside
      if (showMobileNavDropdown && !isMobileDropdownElement) {
        setShowMobileNavDropdown(false);
      }
    };

    if (showProfileMenu || showNotifications || showQuickActions || showMobileNavDropdown) {
      // Use a delay to avoid immediate closure when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside, true);
      }, showMobileNavDropdown ? 200 : 0);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside, true);
      };
    }
  }, [showProfileMenu, showNotifications, showQuickActions, showMobileNavDropdown]);

  useEffect(() => {
    if (showProfileMenu && profileTriggerRef.current) {
      const rect = profileTriggerRef.current.getBoundingClientRect();
      setProfileDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [showProfileMenu]);

  const handleNavClick = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setShowKeyboardShortcuts(!showKeyboardShortcuts);
      }
      if (e.key === 'Escape') {
        setShowProfileMenu(false);
        setShowNotifications(false);
        setShowQuickActions(false);
        setShowKeyboardShortcuts(false);
        setShowSearch(false);
        setShowMobileNavDropdown(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showKeyboardShortcuts]);

  const fetchTournament = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/api/tournaments/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTournament(res.data.tournament);
    } catch (err) {
      console.error('Error fetching tournament:', err);
      toast.error('Failed to load tournament data');
    }
  }, [code]);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (!token || !storedUser) {
        navigate('/login/tournament-admin', { replace: true });
        return false;
      }
      
      let parsedUser;
      try {
        parsedUser = JSON.parse(storedUser);
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login/tournament-admin', { replace: true });
        return false;
      }
      
      const allowedRoles = new Set(['tournamentadmin', 'superadmin', 'tournamentmanager', 'auctioncontroller']);
      const normalizedRoleKey = (parsedUser?.role || '')
        .toString()
        .trim()
        .replace(/[\s_]/g, '')
        .toLowerCase();
          
      if (!parsedUser || !parsedUser.role || !allowedRoles.has(normalizedRoleKey)) {
        navigate('/login/tournament-admin', { replace: true });
        return false;
      }
      
      return true;
    };

    if (!checkAuth()) {
      return;
    }
    
    fetchTournament();

    const handlePageShow = (e) => {
      if (e.persisted) {
        checkAuth();
      }
    };

    const handleFocus = () => {
      checkAuth();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAuth();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [navigate, code, fetchTournament]);

  useEffect(() => {
    if (!tournament) return;
    const updateCountdown = () => {
      const now = new Date();
      let targetDate;
      if (tournament.status === 'Upcoming' && tournament.startDate) {
        targetDate = new Date(tournament.startDate);
      } else if (tournament.status === 'Active' && tournament.endDate) {
        targetDate = new Date(tournament.endDate);
      } else {
        setCountdown('');
        return;
      }
      const diff = targetDate - now;
      if (diff <= 0) {
        setCountdown('Event Started');
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [tournament]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tournamentCode');
    navigate('/');
    toast.success('Logged out successfully');
  }, [navigate]);

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const sidebarSections = [
    {
      id: 'main',
      label: 'Main',
      items: [
        { 
          icon: '', 
          label: 'Dashboard', 
          path: `/tournament/${code}/overview`, 
          shortcut: '1',
          description: 'Overview and statistics',
          badge: null
        },
        { 
          icon: '', 
          label: 'Players', 
          path: `/tournament/${code}/players`, 
          shortcut: '2',
          description: 'Manage players and registrations',
          badge: null
        },
        { 
          icon: '', 
          label: 'Teams', 
          path: `/tournament/${code}/teams`, 
          shortcut: '3',
          description: 'Team management',
          badge: null
        },
        { 
          icon: '', 
          label: 'Auction', 
          path: `/tournament/${code}/auction`, 
          shortcut: '4',
          description: 'Live auction management',
          badge: quickStats?.auctionProgress > 0 ? `${quickStats.auctionProgress}%` : null,
          highlight: quickStats?.auctionProgress > 0 && quickStats.auctionProgress < 100
        },
      ]
    },
    {
      id: 'analytics',
      label: 'Analytics & Tools',
      items: [
        { 
          icon: '', 
          label: 'Reports', 
          path: `/tournament/${code}/report`, 
          shortcut: '5',
          description: 'View detailed reports',
          badge: null
        },
        { 
          icon: '', 
          label: 'Links', 
          path: `/tournament/${code}/links`, 
          shortcut: '7',
          description: 'Registration and share links',
          badge: null
        },
        { 
          icon: '', 
          label: 'Schedule', 
          path: `/tournament/${code}/schedule`, 
          shortcut: '8',
          description: 'Tournament schedule',
          badge: null
        },
      ]
    },
    {
      id: 'settings',
      label: 'Configuration',
      items: [
        { 
          icon: '', 
          label: 'Settings', 
          path: `/tournament/${code}/settings`, 
          shortcut: 'S',
          description: 'Tournament settings and preferences',
          badge: null
        },
        { 
          icon: '', 
          label: 'Admins', 
          path: `/tournament/${code}/admins`, 
          shortcut: '0',
          description: 'Manage administrators',
          badge: null
        },
      ]
    }
  ];

  const filteredSections = sidebarSections.map(section => {
    if (isTournamentManager) {
      const allowedPaths = [
        `/tournament/${code}/overview`,
        `/tournament/${code}/players`,
        `/tournament/${code}/teams`,
        `/tournament/${code}/report`
      ];
      return {
        ...section,
        items: section.items.filter(item => allowedPaths.includes(item.path))
      };
    }
    if (isAuctionController) {
      const allowedPaths = [
        `/tournament/${code}/overview`,
        `/tournament/${code}/auction`,
        `/tournament/${code}/settings`
      ];
      return {
        ...section,
        items: section.items.filter(item => {
          const itemPath = item.path;
          return allowedPaths.includes(itemPath) || (itemPath.startsWith('/tournament/') && itemPath.includes('/auction'));
        })
      };
    }
    return section;
  }).filter(section => section.items.length > 0);
  
  const allSidebarItems = filteredSections.flatMap(section => section.items);

  // eslint-disable-next-line no-unused-vars
  const favoriteItemsList = allSidebarItems.filter(item => 
    favoriteItems.includes(item.path)
  );

  // eslint-disable-next-line no-unused-vars
  const toggleFavorite = (path) => {
    setFavoriteItems(prev => {
      const newFavorites = prev.includes(path)
        ? prev.filter(p => p !== path)
        : [...prev, path];
      localStorage.setItem('favoriteSidebarItems', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  const modeClass = 'mode-dynamic';
  const showBanner = !hideHeader && !isAuctionPage && !isGroupingPage;
  const currentHeaderOffset = showBanner ? (isMobile ? 64 : 68) : 0;

  const tournamentStatusBadge = (() => {
    const s = tournament?.status;
    if (!s) return { text: '', className: '' };
    if (s === 'Active') return { text: 'Active', className: 'banner-clean__status--active' };
    if (s === 'Upcoming') return { text: 'Upcoming', className: 'banner-clean__status--upcoming' };
    return { text: s, className: 'banner-clean__status--completed' };
  })();

  useEffect(() => {
    if (!showMobileNavDropdown) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showMobileNavDropdown]);

  return (
    <div
      className={noLayoutClasses ? '' : `tournament-admin-layout tournament-admin-modern ${modeClass}`}
      style={{ '--header-offset': `${currentHeaderOffset}px`, '--hero-height': '0px' }}
    >
      {/* Top banner: clean white mode – desktop only full layout; mobile = minimal bar + drawer */}
      {showBanner && (
        <header className="admin-header admin-header--dynamic admin-header--white tournament-admin-top-banner banner-clean-white">
          <div className="banner-clean__content">
            {isMobile && (
              <div className="banner-clean__left banner-clean__left--mobile">
                <button
                  type="button"
                  className="banner-clean__app"
                  onClick={() => navigate(`/tournament/${code}/overview`)}
                  aria-label="Go to tournament overview"
                >
                  {logoUrl ? (
                    <img src={logoUrl} alt="" className="banner-clean__app-img" />
                  ) : (
                    <span className="banner-clean__app-name">{(tournament?.name || code || 'T').charAt(0).toUpperCase()}</span>
                  )}
                </button>
                <div className="banner-clean__title-block">
                  <p className="banner-clean__tournament-title">{tournament?.name || code || 'Tournament'}</p>
                  {tournamentStatusBadge.text ? (
                    <span className={`banner-clean__status ${tournamentStatusBadge.className}`}>{tournamentStatusBadge.text}</span>
                  ) : null}
                </div>
              </div>
            )}
            {/* CENTER – Navigation + Profile (desktop only) */}
            {!isMobile && (
              <nav className="banner-clean__center" aria-label="Main navigation">
                <div className="banner-clean__nav">
                  {filteredSections.map(section =>
                    section.items.map(item => (
                      <button
                        key={item.path}
                        type="button"
                        className={`banner-clean__nav-link ${isActiveRoute(item.path) ? 'banner-clean__nav-link--active' : ''}`}
                        onClick={() => handleNavClick(item.path)}
                        title={item.description || item.label}
                      >
                        {item.label}
                        {item.badge && (
                          <span className="banner-clean__nav-badge">{item.badge}</span>
                        )}
                      </button>
                    ))
                  )}
                  <div className="banner-clean__nav-profile-wrap" ref={profileMenuRef}>
                    <button
                      ref={profileTriggerRef}
                      type="button"
                      className={`banner-clean__nav-link banner-clean__nav-link--profile ${showProfileMenu ? 'banner-clean__nav-link--active' : ''}`}
                      onClick={() => setShowProfileMenu((v) => !v)}
                      aria-expanded={showProfileMenu}
                      aria-haspopup="true"
                      aria-label="Profile menu"
                      title="Profile and logout"
                    >
                      <span className="banner-clean__avatar banner-clean__avatar--nav" aria-hidden="true">
                        {(user?.username || user?.name || 'A').charAt(0).toUpperCase()}
                      </span>
                      <span className="banner-clean__nav-profile-name">{user?.username || user?.name || 'Admin'}</span>
                      <span className="banner-clean__profile-chevron" aria-hidden="true">▼</span>
                    </button>
                  </div>
                </div>
                {showProfileMenu && createPortal(
                  <div
                    ref={profileDropdownRef}
                    className="banner-clean__profile-dropdown banner-clean__profile-dropdown--portal"
                    role="menu"
                    style={{
                      position: 'fixed',
                      top: profileDropdownPosition.top,
                      right: profileDropdownPosition.right,
                      left: 'auto',
                    }}
                  >
                    <div className="banner-clean__profile-dropdown-header">
                      <span className="banner-clean__avatar banner-clean__avatar--sm">
                        {(user?.username || user?.name || 'A').charAt(0).toUpperCase()}
                      </span>
                      <span className="banner-clean__profile-dropdown-name">{user?.username || user?.name || 'Admin'}</span>
                    </div>
                    <button
                      type="button"
                      className="banner-clean__profile-dropdown-logout"
                      role="menuitem"
                      onClick={() => {
                        setShowProfileMenu(false);
                        handleLogout();
                      }}
                    >
                      Logout
                    </button>
                  </div>,
                  document.body
                )}
              </nav>
            )}

            {/* Mobile: hamburger to open nav drawer */}
            {isMobile && (
              <button
                type="button"
                className="banner-clean__hamburger"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowMobileNavDropdown((v) => !v);
                }}
                aria-expanded={showMobileNavDropdown}
                aria-label="Open menu"
              >
                <span className="banner-clean__hamburger-icon" />
                <span className="banner-clean__hamburger-icon" />
                <span className="banner-clean__hamburger-icon" />
              </button>
            )}
          </div>

          {/* Portal to body: header has backdrop-filter which traps position:fixed to ~64px — nav was invisible */}
          {isMobile &&
            createPortal(
              <>
                <div
                  className={`banner-clean__drawer-overlay ${showMobileNavDropdown ? 'banner-clean__drawer-overlay--open' : ''}`}
                  onClick={() => setShowMobileNavDropdown(false)}
                  onKeyDown={(e) => e.key === 'Escape' && setShowMobileNavDropdown(false)}
                  role="presentation"
                  aria-hidden={!showMobileNavDropdown}
                />
                <div
                  className={`banner-clean__drawer ${showMobileNavDropdown ? 'banner-clean__drawer--open' : ''}`}
                  ref={mobileNavDropdownRef}
                  role="dialog"
                  aria-modal={showMobileNavDropdown ? true : undefined}
                  aria-label="Tournament menu"
                  aria-hidden={!showMobileNavDropdown}
                >
                  <div className="banner-clean__drawer-header">
                    <div className="banner-clean__drawer-header-main">
                      <span className="banner-clean__drawer-title">{tournament?.name || 'Tournament'}</span>
                      {code ? <span className="banner-clean__drawer-code">{code}</span> : null}
                    </div>
                    <button
                      type="button"
                      className="banner-clean__drawer-close"
                      onClick={() => setShowMobileNavDropdown(false)}
                      aria-label="Close menu"
                    >
                      ×
                    </button>
                  </div>
                  <nav className="banner-clean__drawer-nav" aria-label="Main navigation">
                    {filteredSections.map(section => (
                      <div key={section.id} className="banner-clean__drawer-section">
                        {section.label ? (
                          <div className="banner-clean__drawer-section-label">{section.label}</div>
                        ) : null}
                        {section.items.map(item => (
                          <button
                            key={item.path}
                            type="button"
                            className={`banner-clean__drawer-item ${isActiveRoute(item.path) ? 'banner-clean__drawer-item--active' : ''}`}
                            onClick={() => {
                              handleNavClick(item.path);
                              setShowMobileNavDropdown(false);
                            }}
                          >
                            <span className="banner-clean__drawer-item-label">{item.label}</span>
                            {item.badge ? (
                              <span className="banner-clean__drawer-badge">{item.badge}</span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ))}
                  </nav>
                  <div className="banner-clean__drawer-footer">
                    <span className="banner-clean__drawer-user-avatar" aria-hidden="true">
                      {(user?.username || user?.name || 'A').charAt(0).toUpperCase()}
                    </span>
                    <span className="banner-clean__drawer-username">{user?.username || user?.name || 'Admin'}</span>
                    <button
                      type="button"
                      className="banner-clean__drawer-logout"
                      onClick={() => {
                        handleLogout();
                        setShowMobileNavDropdown(false);
                      }}
                    >
                      Log out
                    </button>
                  </div>
                </div>
              </>,
              document.body
            )}
        </header>
      )}

      {/* Main Content */}
      <main
        className={`admin-main admin-main--dynamic ${fullSize ? 'auction-pro-fullsize' : ''}`}
        style={{ 
          marginLeft: '0',
          marginRight: '0',
          width: '100%',
          maxWidth: '100%',
          transition: 'none'
        }}
      >

        {showSearch && (
          <>
            <span className="search-icon">🔍</span>
            <input
              ref={searchInputRef}
              type="text"
              className="search-input"
              placeholder="Search... (⌘K)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSearchQuery('');
                  setShowSearch(false);
                  e.target.blur();
                }
              }}
            />
          </>
        )}

        {showQuickActions && (
          <div className="quick-actions-panel" ref={quickActionsRef}>
            <div className="quick-actions-header">
              <h3>Quick Actions</h3>
              <button onClick={() => setShowQuickActions(false)} className="close-btn">✕</button>
            </div>
            <div className="quick-actions-grid">
              <button 
                className="quick-action-btn"
                onClick={() => { navigate(`/tournament/${code}/players`); setShowQuickActions(false); }}
              >
                <span className="action-icon">➕</span>
                <span className="action-label">Add Player</span>
              </button>
              <button 
                className="quick-action-btn"
                onClick={() => { navigate(`/tournament/${code}/teams`); setShowQuickActions(false); }}
              >
                <span className="action-icon">⚽</span>
                <span className="action-label">Add Team</span>
              </button>
              <button 
                className="quick-action-btn"
                onClick={() => { navigate(`/tournament/${code}/report`); setShowQuickActions(false); }}
              >
                <span className="action-icon">📊</span>
                <span className="action-label">View Reports</span>
              </button>
              <button 
                className="quick-action-btn"
                onClick={() => { navigate(`/tournament/${code}/auction`); setShowQuickActions(false); }}
              >
                <span className="action-icon">🎤</span>
                <span className="action-label">Start Auction</span>
              </button>
              <button 
                className="quick-action-btn"
                onClick={() => { navigate(`/tournament/${code}/links`); setShowQuickActions(false); }}
              >
                <span className="action-icon">🔗</span>
                <span className="action-label">Share Links</span>
              </button>
            </div>
          </div>
        )}

        {children}
      </main>


      {showKeyboardShortcuts && (
        <div className="modal-overlay" onClick={() => setShowKeyboardShortcuts(false)}>
          <div className="modal-content keyboard-shortcuts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Keyboard Shortcuts</h3>
              <button className="modal-close" onClick={() => setShowKeyboardShortcuts(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="shortcuts-grid">
                <div className="shortcut-item">
                  <div className="shortcut-keys">
                    <kbd>⌘</kbd> + <kbd>K</kbd>
                  </div>
                  <div className="shortcut-description">Search</div>
                </div>
                <div className="shortcut-item">
                  <div className="shortcut-keys">
                    <kbd>⌘</kbd> + <kbd>/</kbd>
                  </div>
                  <div className="shortcut-description">Show shortcuts</div>
                </div>
                <div className="shortcut-item">
                  <div className="shortcut-keys">
                    <kbd>Esc</kbd>
                  </div>
                  <div className="shortcut-description">Close modals</div>
                </div>
                <div className="shortcut-item">
                  <div className="shortcut-keys">
                    <kbd>1</kbd> - <kbd>9</kbd>, <kbd>0</kbd>
                  </div>
                  <div className="shortcut-description">Navigate sections</div>
                </div>
                <div className="shortcut-item">
                  <div className="shortcut-keys">
                    <kbd>N</kbd>
                  </div>
                  <div className="shortcut-description">Notifications</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TournamentAdminLayout;
